// 花嫁の回る連撃 (裾乱れ 0〜6段・三重の返礼) が「回って見える」かの試作 2つ (2026-10-08・開発版だけ)。
//
// 経緯 (docs/research/2026-10-05-bride-spin-fx-attempts.md): 前の半円の糸 (src/bride_thread_sweep.ts) も、
//   前だけのリボン (枝 claude/bride-spin-smear-wip-1005) も「回って見えない」。ユーザーの見立て＝
//   ①体が回っていない ②エフェクトが前の片側にしかない。最も近い手本＝DARK SOULS III 冷たい谷の踊り子の回転連撃。
//   問い (A 回転の途中の絵／B 反転・横幅つぶしで回す／C 全周の層＋前だけ紫／D 回る技をやめる) に
//   ユーザー「CかBの試作を動画で見せてからA」(台帳 docs/ledger/bosses.md 2026-10-05)。
//
// 開発版の切り替え ?brideSpin=now|b|c|bc|ca (window.__setBrideSpin でも)。無し／他の値＝配布版の既定 (ca)。
// 🔵 2026-10-09 配布版の既定を ca へ (ユーザー：動画で「ca（絵も回る）」→「入れる」)。旧の前の半円の糸＝?brideSpin=now。
//   b  = 体を回す: 今ある絵 (右・交差・左の差し替えはそのまま) の横幅を cos(θ) でつぶし、背中側の半分は左右反転して少し暗く。
//   c  = 全周の層: 体の周りを回る白い風 (奥の半分は体の後ろ・手前の半分は前) ＋向いている側だけの紫の三日月の膜 (段ごと)。
//        見本 = そざい/fx/bride-flurry-fx-1005/C_front.png・C_behind.png。c では前の半円の糸を描かない (紫の膜が置き換える)。
//   bc = 両方。
//   ca = C ＋ 回る途中の絵 (A・10-09 生成の 真横／斜め後ろ／真後ろ の3枚と、その左右反転)。体の向きの角で絵を差し替える。
//        ユーザー (10-09)「C」「A を描き足す＝はい」「板が回ってる用に見えているから回す方式が合わないようなら
//        今まで通り切り替えだけで、エフェクトだけで回転を表現したほうが良いかも知れない」。
// 🔴 見た目だけ。当たり・時間・ダメージ・AI はどのモードでも同じ (tests/bride_spin_modes.test.ts)。
//   紫の膜の外の端 = THREAD_REACH_PX (135)。当たりの前端 150 = これ + 15 (attacks.ts)。背中側には紫を出さない。
import type { Boss } from './types'
import { PALETTE } from './balance'
import { sw as swc } from './purple_swap'   // 紫の技を替える比較版 (DEV ?purpleSwap)。裾乱れは弾ける技＝'parry'
import { THREAD_REACH_PX } from './bride_thread_sweep'

export type BrideSpinMode = 'now' | 'b' | 'c' | 'bc' | 'ca'
export const BRIDE_SPIN_MODES: readonly BrideSpinMode[] = ['now', 'b', 'c', 'bc', 'ca']
export function parseBrideSpinMode(v: unknown): BrideSpinMode | null {
  const k = typeof v === 'string' ? v.toLowerCase() : ''
  return (BRIDE_SPIN_MODES as readonly string[]).includes(k) ? k as BrideSpinMode : null
}
let mode: BrideSpinMode = 'ca'
export function brideSpinMode(): BrideSpinMode { return mode }
/** テストと DEV の切り替え用。知らない値は今のまま。 */
export function setBrideSpinMode(v: unknown): BrideSpinMode { mode = parseBrideSpinMode(v) ?? mode; return mode }
export function brideSpinBodyOn(m: BrideSpinMode = mode): boolean { return m === 'b' || m === 'bc' }
export function brideSpinLayersOn(m: BrideSpinMode = mode): boolean { return m === 'c' || m === 'bc' || m === 'ca' }
export function brideSpinTurnArtOn(m: BrideSpinMode = mode): boolean { return m === 'ca' }

// ---------------------------------------------------------------- 回転の時計
// 回る段の数と、その間に回る回数。裾乱れ = 0〜6段 (7段) で4回転 ≒ 2段で1回転強 (段の間隔 250ms → 1回転 ≒ 0.45秒)。
//   三重の返礼 = 3段で2回転。最後の段の後は正面で止まる (回数が整数 = 正面で終わる)。
const SPIN_PLAN: Record<string, { hits: number; revs: number }> = {
  veilFlurry: { hits: 7, revs: 4 },
  veilBlinkTriple: { hits: 3, revs: 2 },
}
/** 最後の段の「1段ぶん」の長さ = 当たりの長さ + これ (ms)。ここで回り終えて正面に戻る。 */
const LAST_TAIL_MS = 90

export interface BrideSpinClock {
  /** 回り始め (0段目の当たりの頭) からの段の数 (小数)。最後の段を過ぎても増え続ける (風が消える時計)。 */
  units: number
  hits: number
  revs: number
  /** 回り始めからの ms。 */
  ms: number
}

/** 回る連撃の最中なら時計、そうでなければ null (体は正面・層は無し)。状態を持たない = ボスの状態だけで決まる。 */
export function brideSpinClock(b: Boss): BrideSpinClock | null {
  if (b.id !== 'bride' || b.hp <= 0) return null
  const atk = b.currentAttack
  const plan = atk ? SPIN_PLAN[atk.id] : undefined
  if (!atk || !plan) return null
  const k = b.comboHitIndex
  if (k < 0 || k >= plan.hits) return null
  let inHit: number
  if (b.state === 'active') inHit = b.stateElapsed * 1000
  else if (b.state === 'comboGap' || (b.state === 'recovery' && k === plan.hits - 1)) inHit = atk.activeMs + b.stateElapsed * 1000
  else return null
  const gap = (j: number) => atk.comboGapsMs?.[j] ?? atk.comboGapMs ?? 0
  const unitMs = (j: number) => (j < plan.hits - 1 ? atk.activeMs + gap(j) : atk.activeMs + LAST_TAIL_MS)
  let ms = 0
  for (let j = 0; j < k; j++) ms += atk.activeMs + gap(j)
  ms += inHit
  return { units: k + inHit / unitMs(k), hits: plan.hits, revs: plan.revs, ms }
}

// 正面で少しゆっくり・背中で速く回す (0.45 = 正面の速さ 0.55倍・背中 1.45倍)。正面の絵が読める時間を長く、背中は素早く抜ける。
const DWELL = 0.45
/** 体の向きの角 (0 = 正面・π = 背中)。回り終えると 2π の整数倍 = 正面。 */
export function brideSpinAngle(c: BrideSpinClock): number {
  const u = Math.max(0, Math.min(1, c.units / c.hits))
  const r = c.revs * u
  return 2 * Math.PI * r - DWELL * Math.sin(2 * Math.PI * r)
}

/** 横幅の最小 (真横でも絵が消えない)。 */
export const SPIN_MIN_WIDTH = 0.18
/** 角 θ の体の横の倍率 (負 = 左右反転 = 背中) と、背中らしさ 0..1 (暗く・色を落とす量)。 */
export function brideSpinBodyScale(theta: number): { sx: number; back: number } {
  const c = Math.cos(theta)
  const sign = c < 0 ? -1 : 1
  return { sx: sign * Math.max(SPIN_MIN_WIDTH, Math.abs(c)), back: Math.max(0, -c) }
}
/** 背中側の暗さ (ctx.filter)。正面では 'none'。 */
export function brideSpinBackFilter(back: number): string {
  if (back <= 0.001) return 'none'
  return `brightness(${(1 - 0.4 * back).toFixed(3)}) saturate(${(1 - 0.55 * back).toFixed(3)})`
}

// ---------------------------------------------------------------- A 回る途中の絵 (10-09・?brideSpin=ca)
// 絵 = public/sprites/bosses/bride/flurry_turn_{side,back3q,back}_1009 (Codex 生成・票 bride-spin-turn-art-1009・
//   そざい/bosses/bride/2026-10-09-spin-turn/)。元の絵はどれも「横顔・顔が画面左」の向き。反対側は左右反転で作る。
// 回る向き = 白い風と同じ (手前が向いている側へ流れる) → 正面の胸がまず向いている側へ回る = 真横 (向いている側を見る)
//   → 斜め後ろ → 真後ろ → 斜め後ろ (反転) → 真横 (反転) → 正面。
// 測り値 (axisX = 腰の高さの胴の中心・headY = 頭頂・hemY = 裾の一番下。元の絵の px・measure.json)。
export type BrideTurnArt = 'side' | 'back3q' | 'back'
export const BRIDE_TURN_ART: Record<BrideTurnArt, { file: string; axisX: number; headY: number; hemY: number }> = {
  side: { file: 'flurry_turn_side_1009', axisX: 350, headY: 105, hemY: 1456 },
  back3q: { file: 'flurry_turn_back3q_1009', axisX: 422, headY: 24, hemY: 1417 },
  back: { file: 'flurry_turn_back_1009', axisX: 407, headY: 97, hemY: 1446 },
}
/** 体の向きの角 θ で見せる回る途中の絵。null = 正面 (今の右・交差・左の絵のまま)。mirror = 元の絵を左右反転する。 */
export function brideTurnPick(theta: number): { art: BrideTurnArt; mirror: boolean } | null {
  const P = Math.PI
  const t = ((theta % (2 * P)) + 2 * P) % (2 * P)
  if (t < P / 4 || t >= (7 * P) / 4) return null
  if (t < (5 * P) / 8) return { art: 'side', mirror: false }
  if (t < (7 * P) / 8) return { art: 'back3q', mirror: false }
  if (t < (9 * P) / 8) return { art: 'back', mirror: false }
  if (t < (11 * P) / 8) return { art: 'back3q', mirror: true }
  return { art: 'side', mirror: true }
}
const turnImages = new Map<BrideTurnArt, HTMLImageElement>()
/** 読み込み済みなら絵、まだなら null (その間は正面の絵のまま)。最初に呼ばれた時に読み込みを始める。 */
export function brideTurnImage(art: BrideTurnArt): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null
  let img = turnImages.get(art)
  if (!img) {
    img = new Image()
    img.src = `/sprites/bosses/bride/${BRIDE_TURN_ART[art].file}.webp`
    turnImages.set(art, img)
  }
  return img.complete && img.naturalWidth > 0 ? img : null
}
// 10-23 裾を床に着けて描き直した3枚 (開発版 ?brideRedraw=floor・src/bride_redraw_1023.ts)。axisX = 腰の黒い薔薇の x。
const BRIDE_TURN_ART_1009 = { ...BRIDE_TURN_ART }
export const BRIDE_TURN_ART_FLOOR_1023: Record<BrideTurnArt, { file: string; axisX: number; headY: number; hemY: number }> = {
  side: { file: 'flurry_turn_side_floor_1023', axisX: 350, headY: 10, hemY: 1358 },
  back3q: { file: 'flurry_turn_back3q_floor_1023', axisX: 418, headY: 10, hemY: 1377 },
  back: { file: 'flurry_turn_back_floor_1023', axisX: 406, headY: 10, hemY: 1356 },
}
// 10-25 騎士の線・案A で描き直した3枚 (開発版 ?brideArt=a・src/bride_art_sample_1025.ts)。
//   headY/hemY = 絵の縁の上下。axisX = 床版での「軸と腰の黒い薔薇の横のずれ」を、頭〜裾の長さの比で写した所。
export const BRIDE_TURN_ART_LINE_A_1025: Record<BrideTurnArt, { file: string; axisX: number; headY: number; hemY: number }> = {
  side: { file: 'flurry_turn_side_art_1025_a', axisX: 353, headY: 6, hemY: 1402 },
  back3q: { file: 'flurry_turn_back3q_art_1025_a', axisX: 429, headY: 6, hemY: 1400 },
  back: { file: 'flurry_turn_back_art_1025_a', axisX: 454, headY: 6, hemY: 1439 },
}
export type BrideTurnArtSet = 'base' | 'floor' | 'lineA'
/** 回る途中の絵の組を替える (base = 元の3枚・floor = 床に着けた3枚・lineA = 騎士の線 案A)。読み込み済みの絵も捨てて読み直す。 */
export function setBrideTurnArtSet(set: BrideTurnArtSet): void {
  Object.assign(BRIDE_TURN_ART, set === 'lineA' ? BRIDE_TURN_ART_LINE_A_1025 : set === 'floor' ? BRIDE_TURN_ART_FLOOR_1023 : BRIDE_TURN_ART_1009)
  turnImages.clear()
  if (typeof window !== 'undefined') for (const k of Object.keys(BRIDE_TURN_ART) as BrideTurnArt[]) brideTurnImage(k)
}
/** 回る途中の絵を床に着けた3枚へ替える (false で元の3枚)。 */
export function setBrideTurnArtFloor(on: boolean): void {
  setBrideTurnArtSet(on ? 'floor' : 'base')
}
/**
 * 回る途中の絵を描く。frontRect = 同じ瞬間の正面の絵 (右・交差・左) の不透明部分の矩形 (画面座標)。
 * 頭頂〜裾の丈をそれに合わせ、裾の一番下を同じ高さ、胴の中心を axisX に置く。facing = ボスの向き。
 */
export function drawBrideTurnArt(
  ctx: CanvasRenderingContext2D, pick: { art: BrideTurnArt; mirror: boolean }, img: HTMLImageElement,
  axisX: number, frontRect: { y: number; height: number }, facing: 'left' | 'right',
): void {
  const a = BRIDE_TURN_ART[pick.art]
  const s = frontRect.height / (a.hemY - a.headY)
  const mul = (pick.mirror ? -1 : 1) * (facing === 'right' ? -1 : 1)
  ctx.save()
  ctx.translate(axisX, frontRect.y + frontRect.height)
  ctx.scale(mul * s, s)
  ctx.drawImage(img, -a.axisX, -a.hemY)
  ctx.restore()
}

// ---------------------------------------------------------------- C ① 体の周りを回る白い風
// つぶれた楕円 (少し上から見た水平の輪) の上を、細く・ほつれ・途切れた筋が数本回り続ける。
//   奥の半分 (楕円の上半分) は花嫁の絵より先に、手前の半分 (下半分) は後に描く → 体の後ろと前を通る。
//   白・銀・低い不透明度・紫なし = 無害に見える (当たるのは紫の膜の側だけ)。
// 筋: y = 輪の中心 (体の高さの真ん中) からの縦ずれ (体の高さ比・+ = 下)、rx = 横の半径 (体の高さ比)、
//   len = 筋の長さ (角)、spd = 回る速さの倍率、ph = 位相、a = 濃さ、w = 太さ、tilt = 輪の傾き (奥へ行くほど上がる量)。
const WIND_STRANDS = [
  { y: 0.36, rx: 0.64, len: 2.6, spd: 0.92, ph: 0.4, a: 0.62, w: 2.6, tilt: 0.05 },
  { y: 0.18, rx: 0.58, len: 2.0, spd: 1.08, ph: 2.6, a: 0.55, w: 2.0, tilt: -0.08 },
  { y: 0.02, rx: 0.55, len: 2.3, spd: 1.0, ph: 4.4, a: 0.58, w: 2.2, tilt: 0.1 },
  { y: -0.14, rx: 0.52, len: 1.8, spd: 1.16, ph: 1.3, a: 0.5, w: 1.7, tilt: -0.05 },
  { y: -0.27, rx: 0.46, len: 1.5, spd: 0.96, ph: 3.5, a: 0.42, w: 1.4, tilt: 0.12 },
]
const WIND_SQUASH = 0.2          // 輪の縦/横 (見下ろす角度)
const WIND_REV_S = 0.44          // 風の1周の秒 (体の回転とほぼ同じ速さ)
const WIND_IN_UNITS = 0.35       // 回り始めの立ち上がり (段の数)
const WIND_OUT_UNITS = 1.3       // 最後の段の後に消えるまで (段の数)
const WIND_SAMPLES = 26

function hash01(n: number): number { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }

/** 風の濃さ (0..1)。回り始めで立ち上がり、最後の段の後で消える。 */
export function brideSpinWindEnvelope(c: BrideSpinClock): number {
  const fin = Math.min(1, c.units / WIND_IN_UNITS)
  const fout = Math.max(0, Math.min(1, 1 - (c.units - c.hits) / WIND_OUT_UNITS))
  return fin * fout
}

/**
 * 体の周りを回る白い風。pass = 'back' (花嫁の絵の前に呼ぶ = 奥の半分) / 'front' (絵の後に呼ぶ = 手前の半分)。
 * x = 体の中心、footY = 足元、h = 体の表示の高さ。
 */
export function drawBrideSpinWind(
  ctx: CanvasRenderingContext2D, b: Boss, x: number, footY: number, h: number, pass: 'back' | 'front',
): void {
  const c = brideSpinClock(b)
  if (!c) return
  const env = brideSpinWindEnvelope(c)
  if (env <= 0.01) return
  const dir = b.facing === 'right' ? 1 : -1
  const t = c.ms / 1000
  const cy = footY - h * 0.47
  if (windLook !== 'line') {
    preloadBrideWindCloth()
    if (clothReady()) { drawBrideSpinWindCloth(ctx, x, cy, h, dir, t, env, pass); return }
  }
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.lineCap = 'round'
  WIND_STRANDS.forEach((s, si) => {
    const rx = h * s.rx
    const ry = rx * WIND_SQUASH
    // 手前 (sin φ > 0) が向いている側へ流れる = φ が減る向き (x = dir·rx·cos φ)。
    const head = s.ph - (2 * Math.PI / WIND_REV_S) * s.spd * t
    const at = (u: number, rOff: number): [number, number] => {
      const phi = head + s.len * u
      return [x + dir * (rx + rOff) * Math.cos(phi), cy + h * s.y + (ry + rOff * WIND_SQUASH) * Math.sin(phi) - s.tilt * rx * Math.cos(phi)]
    }
    // 筋 1本 = 薄い霞の帯 + 芯 + 毛羽2本 (半径を少しずらした細い線)。
    for (let hair = -1; hair < 3; hair++) {
      const rOff = hair <= 0 ? 0 : (hair === 1 ? 3.5 : -3)
      const hairA = hair === -1 ? 0.16 : hair === 0 ? 1 : 0.5
      const hairW = hair === -1 ? 5 : hair === 0 ? 1 : 0.5
      // 毛羽は芯より短い (先も後ろもほつれて途切れる)。
      const from = hair <= 0 ? 0 : 0.08 + 0.12 * hash01(si * 7 + hair)
      const to = hair <= 0 ? 1 : 0.55 + 0.3 * hash01(si * 13 + hair * 3)
      for (let i = 0; i < WIND_SAMPLES; i++) {
        const u0 = from + (to - from) * (i / WIND_SAMPLES)
        const u1 = from + (to - from) * ((i + 1) / WIND_SAMPLES)
        // ところどころ途切れる (筋ごとに決まった切れ目・少しずつ流れる)。
        if (hair >= 0 && hash01(si * 31 + hair * 5 + Math.floor((u0 + t * 0.6) * 9)) < 0.2) continue
        const phiMid = head + s.len * (u0 + u1) / 2
        const near = Math.sin(phiMid) >= 0
        if ((pass === 'front') !== near) continue
        // 先頭 (u=0) が濃く、尾は薄く細く。奥の半分は少し薄い (奥行き)。横の端 (真横) では少し薄れる。
        const tail = Math.pow(1 - (u0 + u1) / 2, 0.9)
        const depth = near ? 1 : 0.62
        ctx.globalAlpha = s.a * hairA * tail * depth * env
        ctx.lineWidth = s.w * hairW * (0.45 + 0.75 * tail)
        ctx.strokeStyle = hair === -1 || (si + hair) % 2 === 0 ? '#f4f0ff' : PALETTE.fxSilver
        const [x0, y0] = at(u0, rOff), [x1, y1] = at(u1, rOff)
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke()
      }
    }
  })
  ctx.restore()
}

// 風の帯の見た目 (10-31・作者の答え A・配布版の既定 = cloth)。
//   作者 10-31 のメモ原文「それぞれのキャラクターの武器や振っているものの材質に合わせてエフェクトを作ってほしい」。
//   花嫁が振っているのは薄いヴェールの布 → 白い線の筋を、Codex が描いた透けた薄布の帯 (ちぎれた縁・白〜灰) に替える。
//   絵 = public/fx/bride/bride_wind_0..2 (濃・中・薄・元 = docs/research/2026-10-31-gate6-tickets/art/bride_spin_wind_sheet.png)。
//   形 (体の周りの楕円)・回る速さ・出る消えるの時機・紫の三日月の膜・当たりは前の線の風と同じ。帯は筋の通り道を細い短冊に分けて沿わせる。
//   前の線の風 = 開発版 ?brideWind=line。絵が読めていない間 (読み込み中・Vitest) も線で描く。
// 11-01 作者「紫と白2つ出てるのが変に感じる。どちらか一方を消すとかじゃなくて改めて考え直してほしい」→ 比較版 3つ (開発版だけ)。
//   どれも紫の三日月・紫の帯をコードの板で描かず、同じ薄布の絵を紫に染めて使う (1つの布の考えでまとめる)。紫 = 弾ける印は残す。
//   one   = 一枚のヴェール: 白い風5本と紫を、2枚の大きな布の帯にまとめる。背中は白く細く、前を通る時に紫に染まって届きまで伸びる。
//   edge  = 白い風のまま、打つ瞬間だけ同じ布が紫に染まって前へ振り出す (紫の一周の帯は出さない)。
//   lilac = 風も振り出しも薄紫の同じ布 (白を使わない・前で振る布だけ濃い紫)。
//   記録 docs/research/2026-11-01-bride-wind-rethink.md。
// 11-02 作者「3案はどれも違う」→ ほかの作品の回る技を調べた型 (docs/research/2026-11-01-spin-attack-vfx-references.md) で比較版 2つ (開発版だけ)。
//   型 =「跡は当てている物 (ヴェールの布) の縁が通った所だけに、布の色 (白) で出す。弾ける印の紫は跡に塗らず、溜めの終わりに一度だけ光らせる」。
//   bc = 案B＋C: 体のまわりの風をやめる。打つ瞬間だけ前へ白い布の三日月、最後のだんは大きい三日月と、ちぎれた布の切れ端が落ちる。
//   ac = 案A＋C: 体のまわりの風をやめ、裾の高さを白い布の輪が体の回りに付いて流れる (裾の縁の通り道)。打つ瞬間は裾の縁から白い三日月。
//   どちらも紫は、溜めの終わりに裾が一度だけ紫に光る (drawBrideHemFlash・render.ts)。当たり・時間は変えない。
// 11-02 作者がお手本6枚から「裾の布そのもの」を選んだ → hem (開発版だけ・記録 docs/research/2026-11-02-bride-spin-hem.md)。
//   別の物を足さず、裾とヴェールが回る力で円盤のように開き、通った所に同じ布の薄い残像が2枚重なる。三日月・風・紫の帯は出さない。
//   布の絵 = public/fx/bride/bride_hem_0..2 (裾・裾の別のちぎれ方・ヴェール。元 = Codex の hem_cloth_sheet)。紫は溜めの終わりの光だけ (bc/ac と同じ)。
// 11-03 作者「ケバケバした白いウニ」(縁のギザギザが針に見える・布の枚数と残像が重なって毛羽立つ) → hemsoft (開発版だけ・記録 docs/research/2026-11-03-bride-spin-hemsoft.md)。
//   絵 = bride_hem_soft_0..2 (元の絵の縁をなめらかな波に切り直し、外の縁を紗のように薄くした物・scripts/soften_bride_hem_1103.py)。
//   残像は2枚 → 1枚 (薄く・ずれも小さく)、ヴェールの残像はなし。高さ・だんで低くする・紫の光は hem のまま。
// 11-02 作者（hemsoft の答え）「本人が回ってる周りで別のものが回ってるので、そうじゃない…お手本の絵は、本人だけが回っていて、
//   周りにあるエフェクトは残像的」→ ghost (開発版だけ・記録 docs/research/2026-11-03-bride-spin-ghost.md)。
//   体のまわりを回る輪をやめ、本人の回る途中の絵の残像 (少し前の向き・白く薄く・少し広げて後ろへずらす) と、
//   体の左右の縁から外へ流れて消える裾とヴェールの尾 (体に付いたまま・回らない) で見せる。だん・紫の光は hem のまま。
// 11-02 作者（ghost の答え）ghost=ok・trail=none・「残像がもっと濃い版もみてみたい」→ 布の尾をやめ、残像を濃くした ghostdense を足す
//   (開発版だけ・記録 docs/research/2026-11-03-bride-spin-ghost-dense.md)。
export type BrideWindLook = 'cloth' | 'line' | 'one' | 'edge' | 'lilac' | 'bc' | 'ac' | 'hem' | 'hemlow' | 'hemsoft' | 'ghost' | 'ghostdense'
const WIND_LOOKS: readonly BrideWindLook[] = ['cloth', 'line', 'one', 'edge', 'lilac', 'bc', 'ac', 'hem', 'hemlow', 'hemsoft', 'ghost', 'ghostdense']
/** 11-02 の型の比較版 (bc / ac) と、その型でお手本から作った hem か (どれも紫は溜めの終わりの光だけ)。 */
export function brideWindTypeOn(): boolean { return windLook === 'bc' || windLook === 'ac' || hemOn() }
/** hem = 裾の布そのもの。hemlow = 11-02 の最初の版 (裾を膝の高さで回す・作者「足元で回ってる」で胸元へ上げた)。hemsoft = 縁を丸めた hem。 */
function hemOn(): boolean { return windLook === 'hem' || windLook === 'hemlow' || windLook === 'hemsoft' || brideGhostOn() }
/** ghost = 本人の残像だけ (11-02 作者の答え)。ghostdense = 残像を濃くした版。 */
export function brideGhostOn(): boolean { return windLook === 'ghost' || windLook === 'ghostdense' }
// 11-03 作者（ghost の答え）「根本的な回答です」pick=ghost → 配布版の既定。?brideWind=cloth で前の見た目 (開発版)。
let windLook: BrideWindLook = 'ghost'
export function setBrideWindLook(v: unknown): BrideWindLook {
  windLook = (WIND_LOOKS as readonly unknown[]).includes(v) ? v as BrideWindLook : 'ghost'
  return windLook
}
export function brideWindLook(): BrideWindLook { return windLook }
const clothImgs: (HTMLImageElement | null)[] = [null, null, null]
let clothStarted = false
export function preloadBrideWindCloth(prefix = '/fx/bride/'): void {
  if (clothStarted || typeof Image === 'undefined') return
  clothStarted = true
  clothImgs.forEach((_, i) => {
    const img = new Image()
    img.onload = () => { clothImgs[i] = img }
    img.src = `${prefix}bride_wind_${i}.png`
  })
}
function clothReady(): boolean { return clothImgs.every(i => i !== null) }
// 筋ごとの帯 (0 濃・1 中・2 薄) と帯の太さ (体の高さ比)。下の大きな輪ほど濃く太い。
const WIND_CLOTH = [
  { band: 0, th: 0.15 }, { band: 1, th: 0.11 }, { band: 0, th: 0.12 }, { band: 1, th: 0.09 }, { band: 2, th: 0.08 },
]
const CLOTH_SEGS = 28

function drawBrideSpinWindCloth(
  ctx: CanvasRenderingContext2D, x: number, cy: number, h: number, dir: number, t: number, env: number, pass: 'back' | 'front',
): void {
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  WIND_STRANDS.forEach((s, si) => {
    const cl = WIND_CLOTH[si]!
    const img = windLook === 'lilac' ? tintedCloth(cl.band, LILAC) : clothImgs[cl.band]!
    const [iw, ih] = clothSize(img)
    const rx = h * s.rx
    const ry = rx * WIND_SQUASH
    const head = s.ph - (2 * Math.PI / WIND_REV_S) * s.spd * t
    const at = (u: number): [number, number] => {
      const phi = head + s.len * u
      return [x + dir * rx * Math.cos(phi), cy + h * s.y + ry * Math.sin(phi) - s.tilt * rx * Math.cos(phi)]
    }
    const bandH = h * cl.th
    for (let i = 0; i < CLOTH_SEGS; i++) {
      const u0 = i / CLOTH_SEGS, u1 = (i + 1) / CLOTH_SEGS
      const near = Math.sin(head + s.len * (u0 + u1) / 2) >= 0
      if ((pass === 'front') !== near) continue
      // 先頭 (u=0) が濃く太く、尾は薄く細く。奥の半分は少し薄い (奥行き)。
      const tail = Math.pow(1 - (u0 + u1) / 2, 0.8)
      const depth = near ? 1 : 0.6
      ctx.globalAlpha = Math.min(1, s.a * 1.25 * tail * depth * env)
      if (ctx.globalAlpha <= 0.01) continue
      const [x0, y0] = at(u0), [x1, y1] = at(u1)
      const segLen = Math.hypot(x1 - x0, y1 - y0)
      if (segLen < 0.5) continue
      const th = bandH * (0.55 + 0.45 * tail)
      ctx.save()
      ctx.translate(x0, y0)
      ctx.rotate(Math.atan2(y1 - y0, x1 - x0))
      // 継ぎ目が割れないよう少しだけ重ねる (重ね合わせは普通の重ね = 継ぎ目が光らない)。
      ctx.drawImage(img, (iw * i) / CLOTH_SEGS, 0, iw / CLOTH_SEGS, ih, 0, -th / 2, segLen + 0.6, th)
      ctx.restore()
    }
  })
  ctx.restore()
}

// ---------------------------------------------------------------- 11-01 比較版 (one / edge / lilac) の布
type ClothSrc = HTMLImageElement | HTMLCanvasElement
function clothSize(img: ClothSrc): [number, number] {
  return 'naturalWidth' in img ? [img.naturalWidth, img.naturalHeight] : [img.width, img.height]
}
const LILAC = '206,190,238'             // 薄紫の風 (lilac)
const VIOLET = '176,140,232'            // 打つ布の紫 (前の紫の三日月の中ほどの色)
const tintCache = new Map<string, HTMLCanvasElement>()
/** 薄布の絵を色に染めた写し (明るさの濃淡は残す = 掛け合わせ)。紫は ?purpleSwap の比較にも従う。 */
function tintedCloth(band: number, rgb: string): ClothSrc {
  const src = clothImgs[band]!
  if (typeof document === 'undefined') return src
  const col = swc('parry', rgb)
  const key = `${band}|${col}`
  let cv = tintCache.get(key)
  if (!cv) {
    cv = document.createElement('canvas')
    cv.width = src.naturalWidth; cv.height = src.naturalHeight
    const g = cv.getContext('2d')!
    g.drawImage(src, 0, 0)
    g.globalCompositeOperation = 'multiply'
    g.fillStyle = `rgb(${col})`
    g.fillRect(0, 0, cv.width, cv.height)
    g.globalCompositeOperation = 'destination-in'
    g.drawImage(src, 0, 0)
    tintCache.set(key, cv)
  }
  return cv
}

/** 点の列に沿って布の絵を短冊に分けて貼る。th(u) = 太さ px・a(u) = 不透明度・keep(i) = その短冊を描くか。 */
function drawClothAlong(
  ctx: CanvasRenderingContext2D, img: ClothSrc, pts: Array<[number, number]>,
  th: (u: number) => number, a: (u: number) => number, keep: (i: number) => boolean = () => true, twist = false,
): void {
  const [iw, ih] = clothSize(img)
  const n = pts.length - 1
  for (let i = 0; i < n; i++) {
    if (!keep(i)) continue
    const u = (i + 0.5) / n
    const alpha = Math.min(1, a(u))
    if (alpha <= 0.01) continue
    const [x0, y0] = pts[i]!, [x1, y1] = pts[i + 1]!
    const segLen = Math.hypot(x1 - x0, y1 - y0)
    if (segLen < 0.5) continue
    const t = th(u)
    ctx.globalAlpha = alpha
    ctx.save()
    if (twist) {
      // 厚みの向きを縦に寄せ、輪の左右の端 (帯が縦に曲がる所) では帯が細くなって裏返る (布がよじれる)。
      //   回すだけだと端で細切れが扇に開き、絵の中の筋が放射状の針 (ウニ) に見えるため (hemsoft・11-03)。
      const ux = (x1 - x0) / segLen, uy = (y1 - y0) / segLen
      ctx.transform(ux, uy, -uy * 0.3, Math.max(-1, Math.min(1, ux * 2.5)), x0, y0)
    } else {
      ctx.translate(x0, y0)
      ctx.rotate(Math.atan2(y1 - y0, x1 - x0))
    }
    ctx.drawImage(img, (iw * i) / n, 0, iw / n, ih, 0, -t / 2, segLen + 0.6, t)
    ctx.restore()
  }
}

/**
 * one = 一枚のヴェール: 2枚の大きな布の帯が体の周りを回る (時機と道は紫の一周の帯と同じ = 段の当たりの頃に前を通る)。
 * 背中側は白く体のすぐ外を通り、前を通る時だけ紫に染まって届き (THREAD_REACH_PX) まで膨らむ。
 */
function drawBrideOneVeil(
  ctx: CanvasRenderingContext2D, c: BrideSpinClock, x: number, footY: number, h: number, dir: number, env: number, pass: 'back' | 'front',
): void {
  const cy = footY - h * ORBIT_CY
  const ry0 = h * ORBIT_R0 * WIND_SQUASH
  const N = 36
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  for (let k = 0; k < ORBIT_BANDS; k++) {
    const head = brideOrbitHeadAngle(c, k)
    const phis: number[] = [], fronts: number[] = [], pts: Array<[number, number]> = []
    for (let i = 0; i <= N; i++) {
      const u = i / N
      const phi = head + ORBIT_LEN * u
      const r = brideOrbitRadiusPx(phi, h)
      phis.push(phi); fronts.push(brideOrbitFront(phi))
      pts.push([x + dir * r * Math.cos(phi), cy + ry0 * Math.sin(phi) + h * 0.02 * Math.sin(u * 6.3 + c.ms * 0.007 + k)])
    }
    const near = (i: number) => Math.sin((phis[i]! + phis[i + 1]!) / 2) >= 0
    const keep = (i: number) => (pass === 'front') === near(i)
    const fAt = (u: number) => fronts[Math.min(N, Math.round(u * N))]!
    const ends = (u: number) => Math.min(1, u / 0.1) ** 0.6 * Math.pow(1 - u, 0.5)
    const th = (u: number) => h * (0.2 + 0.3 * fAt(u)) * (0.35 + 0.65 * ends(u))
    const base = (u: number) => env * Math.pow(1 - u, 0.5)
    // 白い布と、前ほど濃く紫に染めた同じ布を混ぜる (= 1枚の布の色が前で変わる)。紫は2枚重ねて濃くする (弾ける印)。
    const purple = (u: number) => Math.min(1, fAt(u) * 2.5)
    drawClothAlong(ctx, clothImgs[0]!, pts, th, u => base(u) * (1 - purple(u)), keep)
    drawClothAlong(ctx, tintedCloth(0, VIOLET), pts, th, u => base(u) * purple(u), keep)
    drawClothAlong(ctx, tintedCloth(1, VIOLET), pts, u => th(u) * 0.8, u => base(u) * purple(u) * 0.8, keep)
  }
  ctx.restore()
}

/** edge / lilac の段の振り出し: 前の紫の三日月と同じ形・時機で、紫に染めた薄布を三日月の真ん中の線に沿わせて貼る。white = 染めない白い布 (bc / ac)。 */
function drawBrideClothCrescent(ctx: CanvasRenderingContext2D, age: number, hit: number, white = false): void {
  const fade = veilFade(age)
  ctx.globalCompositeOperation = 'source-over'
  for (let layer = 1; layer >= 0; layer--) {
    const pts = brideVeilCrescent(age, hit, layer)
    if (!pts) continue
    const n = VEIL_SAMPLES + 1
    const mid: Array<[number, number]> = [], thick: number[] = []
    for (let i = 0; i < n; i++) {
      const o = pts[i]!, inn = pts[2 * n - 1 - i]!
      mid.push([(o[0] + inn[0]) / 2, (o[1] + inn[1]) / 2])
      thick.push(Math.max(2, o[0] - inn[0]))
    }
    const img = white ? clothImgs[hit % 2 === 0 ? 0 : 1]! : tintedCloth(hit % 2 === 0 ? 0 : 1, VIOLET)
    const la = layer === 0 ? 1 : 0.45
    drawClothAlong(ctx, img, mid,
      u => thick[Math.min(n - 1, Math.round(u * (n - 1)))]! * 1.25 + 6,
      u => la * fade * 1.15 * Math.pow(Math.sin(Math.PI * u), 0.4))
  }
}

// ---------------------------------------------------------------- C ② 向いている側だけの紫の三日月の膜
// 段ごとに1つ。体の前から外へ振り出して届き (THREAD_REACH_PX) まで行き、約0.25秒で消える。
//   見本 C_front.png = 縁のほつれた紫のヴェールの布が 2〜3枚、外へ向かって重なる (振り出した跡)。
//   形 = 縦に長い三日月 (上下の先は尖る)。外の縁は明るい細い線、中は外ほど濃く体の側へ透ける。
export const VEIL_SWING_S = 0.075   // 外へ振り出し切るまで
export const VEIL_LIFE_S = 0.25     // 段の頭から消えるまで
const VEIL_X0 = 14                  // 三日月の付け根 (体の中心から前へ px)。背中側へは出さない
const VEIL_RY = 64                  // 縦の半径
const VEIL_CY = -52                 // 中心の高さ (FX の起点 = boss.y-50 からの縦ずれ → boss.y-102 = 胸)
const VEIL_THICK = 0.4              // 真ん中の厚み (外の半径に対する比)
// 1枚目 = 振り出している本体 (外の端が届きへ)。2・3枚目 = 通った跡 (内側に残る)。
const VEIL_LAYERS = [
  { r: 1.0, a: 1.0 },
  { r: 0.8, a: 0.42 },
  { r: 0.62, a: 0.24 },
]
// 段ごとに振る面を少し変える (踊り子: 振る面が振りごとに変わる)。
const VEIL_TILTS = [-0.14, 0.12, -0.03, 0.18, -0.2, 0.06, -0.09]
const VEIL_SAMPLES = 24

function easeOutCubic(p: number): number { return 1 - Math.pow(1 - p, 3) }

/** 振り出しの進み (0..1)。 */
function veilSwing(age: number): number { return easeOutCubic(Math.max(0, Math.min(1, age / VEIL_SWING_S))) }
/** 消え方 (1 → 0)。 */
export function veilFade(age: number): number {
  if (age < 0 || age >= VEIL_LIFE_S) return 0
  if (age <= VEIL_SWING_S) return 1
  return Math.pow(1 - (age - VEIL_SWING_S) / (VEIL_LIFE_S - VEIL_SWING_S), 1.3)
}

/**
 * 三日月の1枚の輪郭 (右向きの局所座標・原点 = 体の中心の胸の高さ)。外の縁 (上→下) の後に内の縁 (下→上)。
 * 見えない時は null。テストが「背中側に出ない・届きを越えない」を確かめる (x は 0..THREAD_REACH_PX)。
 */
export function brideVeilCrescent(age: number, hit: number, layer: number): Array<[number, number]> | null {
  const L = VEIL_LAYERS[layer]
  if (!L) return null
  const fade = veilFade(age)
  if (fade <= 0) return null
  const p = veilSwing(age)
  const reach = THREAD_REACH_PX - VEIL_X0
  const head = reach * (0.4 + 0.6 * p)                  // 1枚目の外の半径 (今の位置)
  const R = layer === 0 ? head : reach * L.r              // 跡は決まった位置に残る
  if (layer > 0 && head < R) return null                  // 1枚目がまだそこを通っていない
  const drift = layer === 0 ? 0 : (1 - fade) * 5          // 跡は消える間に少し外へ流れる (1枚目は届きで止まったまま)
  const tilt = VEIL_TILTS[((hit % VEIL_TILTS.length) + VEIL_TILTS.length) % VEIL_TILTS.length]!
  const ry = VEIL_RY * (0.72 + 0.28 * (R / reach))
  const ct = Math.cos(tilt), st = Math.sin(tilt)
  const place = (lx: number, ly: number): [number, number] => {
    const x = VEIL_X0 + lx * ct - ly * st
    return [Math.max(0, Math.min(THREAD_REACH_PX, x)), lx * st + ly * ct]
  }
  // 段ごとに上から下・下から上へ交互に走る: 先頭 (得物) が通った所までの布だけがある。
  const q = Math.max(0.02, Math.min(1, age / (VEIL_SWING_S * 1.2)))
  const down = hit % 2 === 0
  const vFrom = down ? -1 : 1 - 2 * q, vTo = down ? -1 + 2 * q : 1
  const vAt = (i: number) => vFrom + ((vTo - vFrom) * i) / VEIL_SAMPLES
  const out: Array<[number, number]> = []
  for (let i = 0; i <= VEIL_SAMPLES; i++) {
    const v = vAt(i)
    const cv = Math.sqrt(Math.max(0, 1 - v * v))
    out.push(place((R + drift) * Math.pow(cv, 0.85), ry * v))
  }
  for (let i = VEIL_SAMPLES; i >= 0; i--) {
    const v = vAt(i)
    const cv = Math.sqrt(Math.max(0, 1 - v * v))
    out.push(place((R + drift) * (1 - VEIL_THICK) * Math.pow(cv, 1.25), ry * v))
  }
  return out
}

/** 段の紫の三日月の膜。x,y = FX の起点 (ボスに付いて動く・boss.y-50)、age = 段の頭からの秒、dir = 向き、hit = 段の番号。 */
export function drawBrideSpinVeil(
  ctx: CanvasRenderingContext2D, x: number, y: number, age: number, dir: number, hit: number, seed: number,
): void {
  const fade = veilFade(age)
  if (fade <= 0) return
  // hem は裾そのものが打つので三日月を出さない (段の頭で裾が開くのが当たりの印)。
  if (hemOn()) return
  // 11-02 比較版 bc / ac: 白い布の三日月 (たん＝大きく・たたたたた＝小さく速く)。ac は裾の縁から出るので低く平たい。
  if (brideWindTypeOn()) {
    preloadBrideWindCloth()
    if (clothReady()) {
      ctx.save()
      ctx.translate(x, y + VEIL_CY)
      ctx.scale(dir, 1)
      const small = hit >= 1 && hit <= 6 ? 0.82 : 1
      if (windLook === 'ac') { ctx.translate(0, 58); ctx.scale(small, small * 0.62) } else ctx.scale(small, small)
      drawBrideClothCrescent(ctx, age, hit, true)
      ctx.restore()
      return
    }
  }
  // 11-01 比較版: one は前を通る布そのものが紫に染まるので三日月を出さない。edge・lilac は同じ布を紫に染めて振り出す。
  const clothLook = windLook === 'one' || windLook === 'edge' || windLook === 'lilac'
  if (clothLook) preloadBrideWindCloth()
  if (clothLook && clothReady() && windLook === 'one') return
  ctx.save()
  ctx.translate(x, y + VEIL_CY)
  ctx.scale(dir, 1)
  if (clothLook && clothReady()) { drawBrideClothCrescent(ctx, age, hit); ctx.restore(); return }
  for (let layer = VEIL_LAYERS.length - 1; layer >= 0; layer--) {
    const pts = brideVeilCrescent(age, hit, layer)
    if (!pts) continue
    const L = VEIL_LAYERS[layer]!
    const a = L.a * fade
    const n = VEIL_SAMPLES + 1
    // 外の縁を少しだけ内側へ削ってほつれさせる (消える間にほつれが増える)。
    const jag = (i: number) => (hash01(seed * 0.37 + layer * 17 + i * 3.1) - 0.3) * (2 + 7 * (1 - fade))
    const outer = pts.slice(0, n).map(([px, py], i) => [Math.max(0, px - Math.max(0, jag(i))), py] as [number, number])
    const inner = pts.slice(n)
    const maxX = Math.max(...outer.map(q => q[0]))
    const innerMax = Math.max(...inner.map(q => q[0]))
    // 布: 内の縁 (体の側) は透け、外の縁ほど濃い紫。
    const g = ctx.createLinearGradient(Math.max(0, innerMax - 18), 0, Math.max(innerMax - 17, maxX), 0)
    g.addColorStop(0, `rgba(${swc('parry', '154,122,196')},0)`)
    g.addColorStop(0.45, `rgba(${swc('parry', '154,122,196')},0.3)`)
    g.addColorStop(0.85, `rgba(${swc('parry', '176,140,232')},0.62)`)
    g.addColorStop(1, `rgba(${swc('parry', '200,176,236')},0.8)`)
    ctx.globalCompositeOperation = 'lighter'
    ctx.globalAlpha = a
    ctx.fillStyle = g
    ctx.beginPath()
    outer.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)))
    inner.forEach(([px, py]) => ctx.lineTo(px, py))
    ctx.closePath()
    ctx.fill()
    // 外の縁: 明るい細い線 (途切れ途切れ) ＋ 薄い滲み。
    ctx.strokeStyle = swc('parry', PALETTE.fxVioletBright)
    ctx.lineCap = 'round'
    for (let i = 0; i < n - 1; i++) {
      if (hash01(seed + layer * 11 + i * 7.7) < 0.03 + 0.35 * (1 - fade)) continue
      const v = Math.max(-1, Math.min(1, (outer[i]![1] + outer[i + 1]![1]) / 2 / VEIL_RY))
      const taper = Math.pow(Math.max(0, 1 - v * v), 0.6)
      ctx.globalAlpha = a * (0.35 + 0.6 * taper)
      ctx.lineWidth = 0.8 + 1.6 * taper
      ctx.beginPath(); ctx.moveTo(outer[i]![0], outer[i]![1]); ctx.lineTo(outer[i + 1]![0], outer[i + 1]![1]); ctx.stroke()
    }
    // ほつれた糸: 外の縁から内側へ短く垂れる細い線。
    ctx.strokeStyle = swc('parry', PALETTE.fxViolet)
    for (let k = 0; k < 9; k++) {
      const i = 2 + Math.floor(hash01(seed * 1.3 + layer * 5 + k) * (n - 4))
      const [px, py] = outer[i]!
      const l = 6 + 14 * hash01(seed + k * 2.3 + layer)
      ctx.globalAlpha = a * 0.5
      ctx.lineWidth = 0.7
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(Math.max(0, px - l), py + (hash01(k + seed) - 0.5) * 6); ctx.stroke()
    }
  }
  ctx.restore()
}

// ---------------------------------------------------------------- C ③ 白い風と一緒に一周する紫の帯 (10-09)
// ユーザー (10-09)「前だけ紫のエフェクトを出しているから前だけ引っ掻いているような技に見えていると思う」
//   → 「紫も一緒に一周させる」。段ごとの紫の三日月 (C ②) はそのまま前で振り出し、そこから細い紫の布が風と同じ輪を
//   同じ向きに回って体の後ろを通り、次の段の三日月へ繋がる。前を通る時だけ少し太く濃く・届き (135) まで伸びる。
//   背中側は体のすぐ周りを細く薄く通るだけ (当たりは今のまま前だけ)。帯は2本 (反対側同士) で、段の当たりの頃に
//   どちらかが前を通る (π / 段)。状態を持たない = ボスの状態だけで決まる。
//   1回目 (帯を前で太くした版) は前の頂点で縦に平らな四角い板に見えた (10-09 撮影) → 前の形は三日月に任せ、帯は細い布にした。
// 開発版 ?brideVeil=front で 10-08 の「前だけの紫の三日月」だけ (帯なし) へ戻す。
export type BrideVeilStyle = 'orbit' | 'front'
let veilStyle: BrideVeilStyle = 'orbit'
export function brideVeilStyle(): BrideVeilStyle { return veilStyle }
export function setBrideVeilStyle(v: unknown): BrideVeilStyle {
  if (v === 'orbit' || v === 'front') veilStyle = v
  return veilStyle
}

const ORBIT_BANDS = 2
const ORBIT_PEAK_UNITS = 0.3     // 段の頭からこれだけ (段の数) 進んだ所で前を通る = 前の三日月が振り出し切った頃
const ORBIT_LEN = 2.2            // 帯の長さ (角)。先頭 (u=0) から後ろへ尾を引く
export const ORBIT_R0 = 0.52     // 背中側を通る時の半径 (体の高さ比)。裾の幅 (±70px) より外 = 体の白に紛れない (0.36 は撮影で見えなかった)
const ORBIT_CY = 0.55            // 帯の中心の高さ (足元から・体の高さ比) = 胸〜腰
const ORBIT_HW0 = 0.045          // 縦の半幅 (背中側・体の高さ比)。168px の体で約6px = 細い布
const ORBIT_HW1 = 0.09           // 縦の半幅 (前の頂点)。約15px。前の形は段の三日月が持つ
const ORBIT_SAMPLES = 40

/** 帯 k の先頭の角 (風と同じ φ: 0 = 向いている側・+π/2 = 手前・−π/2 = 奥)。風と同じく φ が減る向きへ回る。 */
export function brideOrbitHeadAngle(c: BrideSpinClock, k: number): number {
  return -Math.PI * (c.units - ORBIT_PEAK_UNITS) + k * Math.PI
}
/** 前らしさ 0..1 (向いている側の真横で 1・背中側の半分で 0)。尖らせて、前を通る一瞬だけ伸びる。 */
export function brideOrbitFront(phi: number): number {
  const c = Math.cos(phi)
  return c > 0 ? Math.pow(c, 4) : 0
}
/** 角 φ での帯の半径 (px)。背中側 = 体の高さ×ORBIT_R0、前の頂点 = THREAD_REACH_PX (当たりの前端 150 − 15)。 */
export function brideOrbitRadiusPx(phi: number, h: number): number {
  const r0 = h * ORBIT_R0
  return r0 + (THREAD_REACH_PX - r0) * brideOrbitFront(phi)
}

/**
 * 白い風と一緒に回る紫の帯。pass は風と同じ ('back' = 花嫁の絵の前に呼ぶ / 'front' = 後に呼ぶ)。
 * x = 体の中心、footY = 足元、h = 体の表示の高さ。
 */
export function drawBrideSpinOrbitVeil(
  ctx: CanvasRenderingContext2D, b: Boss, x: number, footY: number, h: number, pass: 'back' | 'front',
): void {
  const c = brideSpinClock(b)
  if (!c) return
  const env = brideSpinWindEnvelope(c)
  if (env <= 0.01) return
  const dir = b.facing === 'right' ? 1 : -1
  const cy = footY - h * ORBIT_CY
  const ry0 = h * ORBIT_R0 * WIND_SQUASH
  ctx.save()
  ctx.lineCap = 'round'
  for (let k = 0; k < ORBIT_BANDS; k++) {
    const head = brideOrbitHeadAngle(c, k)
    const pt = (u: number) => {
      const phi = head + ORBIT_LEN * u
      const f = brideOrbitFront(phi)
      const r = brideOrbitRadiusPx(phi, h)
      // 布: 先と尾は細く尖り、間はゆるく波打つ (丈も高さも)。縦の半幅は前ほど大きい。
      const ends = Math.min(1, u / 0.12) ** 0.6 * Math.pow(1 - u, 0.55)
      const wave = Math.sin(u * 9 + c.ms * 0.011 + k * 2.1)
      const hw = h * (ORBIT_HW0 + (ORBIT_HW1 - ORBIT_HW0) * f) * ends * (0.8 + 0.25 * wave)
      const px = x + dir * r * Math.cos(phi)
      const py = cy + ry0 * Math.sin(phi) + h * 0.025 * Math.sin(u * 6.3 + c.ms * 0.007 + k)
      return { phi, f, px, top: py - hw, bot: py + hw }
    }
    for (let i = 0; i < ORBIT_SAMPLES; i++) {
      const u0 = i / ORBIT_SAMPLES, u1 = (i + 1) / ORBIT_SAMPLES
      const p0 = pt(u0), p1 = pt(u1)
      const near = Math.sin((p0.phi + p1.phi) / 2) >= 0
      if ((pass === 'front') !== near) continue
      const f = (p0.f + p1.f) / 2
      const tail = Math.pow(1 - (u0 + u1) / 2, 1.1)
      const depth = near ? 1 : 0.7
      // 布: 背中側は薄く (0.16)、前を通る時は前の三日月と同じ濃さ。
      ctx.globalCompositeOperation = 'lighter'
      ctx.globalAlpha = env * depth * tail * (0.42 + 0.4 * f)
      ctx.fillStyle = f > 0.35 ? `rgb(${swc('parry', '176,140,232')})` : `rgb(${swc('parry', '140,96,214')})`
      ctx.beginPath()
      ctx.moveTo(p0.px, p0.top); ctx.lineTo(p1.px, p1.top); ctx.lineTo(p1.px, p1.bot); ctx.lineTo(p0.px, p0.bot)
      ctx.closePath(); ctx.fill()
      // 上下の縁: 明るい細い線 (ところどころ途切れる)。前ほど明るく太い。
      if (hash01(k * 53 + i * 7.3 + Math.floor(c.units * 3)) < 0.12) continue
      ctx.strokeStyle = swc('parry', PALETTE.fxVioletBright)
      ctx.globalAlpha = env * depth * tail * (0.45 + 0.5 * f)
      ctx.lineWidth = 0.7 + 1.5 * f
      ctx.beginPath(); ctx.moveTo(p0.px, p0.top); ctx.lineTo(p1.px, p1.top); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(p0.px, p0.bot); ctx.lineTo(p1.px, p1.bot); ctx.stroke()
    }
  }
  ctx.restore()
}

// ---------------------------------------------------------------- 11-02 型の比較版 (bc / ac)
/** 裾乱れの最後の段 (だん・hit8) の頭からの秒。だんの当たりと、その後の隙の間だけ。ほかは null。 */
export function brideSlamAgeS(b: Boss): number | null {
  const atk = b.currentAttack
  if (b.id !== 'bride' || atk?.id !== 'veilFlurry' || b.comboHitIndex !== 8) return null
  if (b.state === 'active') return b.stateElapsed
  if (b.state === 'recovery') return atk.activeMs / 1000 + b.stateElapsed
  return null
}

/** だん (hit8) の白い大きい三日月。上から下へ振り下ろす。絵が読めていない時は false (前の縦の弧のまま)。 */
export function drawBrideSlamCloth(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, dir: number): boolean {
  if (!brideWindTypeOn()) return false
  if (hemOn()) return hemReady()   // hem のだんは裾が大きく開く (drawBrideSpinLayers)
  preloadBrideWindCloth()
  if (!clothReady()) return false
  const a = age * 0.75   // 小さい三日月より少し長く残す (約0.33秒)
  if (veilFade(a) <= 0) return true
  ctx.save()
  ctx.translate(x, y + VEIL_CY + 22)
  ctx.scale(dir * 1.12, 1.3)
  drawBrideClothCrescent(ctx, a, 0, true)
  ctx.restore()
  return true
}

// 切れ端 (bc だけ): だんの三日月の外の縁からちぎれた布が、ゆっくり落ちて消える (作者 10-23「上にバサッとなびいたものが重力で落ちていくように消して」)。
//   x = 体から前へ (体の高さ比)・y = 足元から上 (体の高さ比)・w = 幅 (体の高さ比)・vx/vy = 初めの速さ (px/秒・vy は上が負)。
const SCRAPS = [
  { x: 0.5, y: 0.78, w: 0.2, vx: 46, vy: -70, ph: 0.3, band: 0 },
  { x: 0.72, y: 0.62, w: 0.15, vx: 60, vy: -40, ph: 2.1, band: 1 },
  { x: 0.78, y: 0.44, w: 0.17, vx: 38, vy: -20, ph: 4.0, band: 0 },
  { x: 0.62, y: 0.28, w: 0.13, vx: 30, vy: -55, ph: 1.2, band: 2 },
  { x: 0.4, y: 0.6, w: 0.11, vx: 22, vy: -90, ph: 5.1, band: 1 },
  { x: 0.86, y: 0.7, w: 0.12, vx: 52, vy: -60, ph: 3.3, band: 2 },
]
const SCRAP_START_S = 0.06
const SCRAP_END_S = 1.15
function drawBrideSlamScraps(
  ctx: CanvasRenderingContext2D, b: Boss, x: number, footY: number, h: number, imgs: (HTMLImageElement | null)[] = clothImgs,
): void {
  const age = brideSlamAgeS(b)
  if (age === null || age < SCRAP_START_S) return
  preloadBrideWindCloth()
  if (!clothReady()) return
  const dir = b.facing === 'right' ? 1 : -1
  const t = age - SCRAP_START_S
  const TAU = 0.35, VTERM = 85    // 布は空気に止められてゆっくり落ちる
  const k = TAU * (1 - Math.exp(-t / TAU))
  const fade = Math.min(1, t / 0.05) * Math.max(0, Math.min(1, (SCRAP_END_S - age) / 0.4))
  if (fade <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  SCRAPS.forEach((p, i) => {
    const img = (imgs[p.band] ?? clothImgs[p.band])!
    const sx = (img.naturalWidth * (0.15 + 0.12 * i)) % (img.naturalWidth * 0.7)
    const sw = img.naturalWidth * 0.22
    const px = x + dir * (h * p.x + p.vx * k) + 5 * Math.sin(t * 5 + p.ph)
    const py = Math.min(footY - 3, footY - h * p.y + VTERM * t + (p.vy - VTERM) * k)
    const w = h * p.w
    const hh = w * img.naturalHeight / sw
    ctx.save()
    ctx.translate(px, py)
    ctx.rotate(dir * (p.ph + 1.6 * t * Math.sin(p.ph + t * 2)))
    ctx.scale(Math.max(0.2, Math.abs(Math.cos(t * 4.5 + p.ph))), 1)   // ひらひら返る
    ctx.globalAlpha = 0.85 * fade
    ctx.drawImage(img, sx, 0, sw, img.naturalHeight, -w / 2, -hh / 2, w, hh)
    ctx.restore()
  })
  ctx.restore()
}

// 裾の輪 (ac だけ): 裾の縁が回る通り道に、白い布の帯が体の回りに付いて流れる。体の向き θ と同じ速さで回る
//   (胸の向き φ = π/2 − θ・φ は風と同じ: 0 = 向いている側・+π/2 = 手前)。帯は2本 (裾の両側)。段の頭で少し外へ広がる。
const HEM_CY = 0.07      // 輪の中心 (足元から・体の高さ比) = 裾の高さ
const HEM_R = 0.4        // 横の半径 (体の高さ比)。裾の幅より少し外
const HEM_LEN = 1.9      // 帯の長さ (角)
const HEM_TH = 0.075     // 帯の太さ (体の高さ比)
function drawBrideHemRing(
  ctx: CanvasRenderingContext2D, c: BrideSpinClock, x: number, footY: number, h: number, dir: number, env: number, pass: 'back' | 'front',
): void {
  const theta = brideSpinAngle(c)
  const frac = c.units - Math.floor(c.units)
  const flare = 1 + 0.22 * Math.exp(-frac / 0.18)   // 段の頭で裾が外へ開く
  const rx = h * HEM_R * flare
  const ry = rx * 0.22
  const cy = footY - h * HEM_CY
  const N = 30
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  for (let k = 0; k < 2; k++) {
    const head = Math.PI / 2 - theta + k * Math.PI + 0.35
    const phis: number[] = [], pts: Array<[number, number]> = []
    for (let i = 0; i <= N; i++) {
      const phi = head + HEM_LEN * (i / N)
      phis.push(phi)
      pts.push([x + dir * rx * Math.cos(phi), cy + ry * Math.sin(phi) - h * 0.04 * (1 - i / N) * Math.cos(phi)])
    }
    const keep = (i: number) => (pass === 'front') === (Math.sin((phis[i]! + phis[i + 1]!) / 2) >= 0)
    const ends = (u: number) => Math.min(1, u / 0.08) ** 0.6 * Math.pow(1 - u, 0.7)
    drawClothAlong(ctx, clothImgs[k === 0 ? 0 : 1]!, pts,
      u => h * HEM_TH * flare * (0.4 + 0.6 * ends(u)),
      u => env * 0.9 * Math.pow(1 - u, 0.6) * (Math.sin(phis[Math.round(u * N)]!) >= 0 ? 1 : 0.6), keep)
  }
  ctx.restore()
}

// 溜めの終わりに一度だけ裾が紫に光る (C)。光り方の強さ 0..1 (render.ts drawBrideHemFlash が裾の布を染める)。
//   溜め 1.6 秒の終わり 0.34 秒前に灯り、0.1 秒保って、当たりの頭で消える。
export const HEM_FLASH_IN_MS = 340
export function brideHemFlashK(b: Boss): number {
  if (!brideWindTypeOn() || b.id !== 'bride' || b.state !== 'telegraph' || b.currentAttack?.id !== 'veilFlurry') return 0
  const left = b.currentAttack.chargeMs - b.stateElapsed * 1000
  if (left > HEM_FLASH_IN_MS || left < 0) return 0
  const e = HEM_FLASH_IN_MS - left
  if (e < 90) return e / 90
  if (e < 190) return 1
  return Math.max(0, 1 - (e - 190) / (HEM_FLASH_IN_MS - 190))
}

// 裾の円盤 (hem だけ): 裾とヴェールが回る力で開き、体のまわりの平たい楕円に沿って回る。体の向き θ と同じ速さ
//   (φ は裾の輪と同じ: 0 = 向いている側・+π/2 = 手前)。裾は2枚 (半周ずつ・先頭がちぎれた縁)、ヴェールは肩の高さに1枚。
//   残像 = 同じ帯を回る向きの後ろへずらし、薄く・少し外へ。段の頭で裾が外へ開き残像が濃くなる (= そこで当たる)。
//   最後のだんは正面で止まったまま裾が大きく開いて閉じ、ちぎれた切れ端が落ちる。絵の上の縁 = 外の縁 (ちぎれた側)。
// hemsoft は縁を丸めた別の絵 (bride_hem_soft_*)。
const hemImgSets: Record<'hem' | 'soft', (HTMLImageElement | null)[]> = { hem: [null, null, null], soft: [null, null, null] }
const hemStarted = { hem: false, soft: false }
const hemSet = (): 'hem' | 'soft' => windLook === 'hemsoft' || brideGhostOn() ? 'soft' : 'hem'
export function preloadBrideHem(prefix = '/fx/bride/'): void {
  const set = hemSet()
  if (hemStarted[set] || typeof Image === 'undefined') return
  hemStarted[set] = true
  hemImgSets[set].forEach((_, i) => {
    const img = new Image()
    img.onload = () => { hemImgSets[set][i] = img }
    img.src = `${prefix}${set === 'soft' ? 'bride_hem_soft_' : 'bride_hem_'}${i}.png`
  })
}
function hemReady(): boolean { return hemImgSets[hemSet()].every(i => i !== null) }
// 裾: 中心の高さ・横の半径・帯の太さ (体の高さ比)・帯の長さ (角)・枚数・絵
// hem は お手本のように胸元〜体がいちばん横へ広がる高さで回す (作者 11-02「足元で回ってるから…胸元とか1番横に広がっているところに出すのが良さそう」)
const HEM_LAYERS = [
  { cy: 0.55, r: 0.5, th: 0.3, len: 2.6, n: 2, img: 0 },
  { cy: 0.8, r: 0.34, th: 0.15, len: 1.9, n: 1, img: 2 },
]
const HEM_LAYERS_LOW = [
  { cy: 0.2, r: 0.5, th: 0.3, len: 2.6, n: 2, img: 0 },
  { cy: 0.74, r: 0.32, th: 0.15, len: 1.9, n: 1, img: 2 },
]
const HEM_GHOSTS = [
  { lag: 0, grow: 0, a: 1 },
  { lag: 0.5, grow: 0.07, a: 0.42 },
  { lag: 1.0, grow: 0.14, a: 0.2 },
]
// hemsoft: 残像は1枚だけ (薄く・ずれも小さく・段の頭で濃くなる量も半分)。重なりで毛羽立つのを減らす
const HEM_GHOSTS_SOFT = [
  { lag: 0, grow: 0, a: 1 },
  { lag: 0.4, grow: 0.05, a: 0.3 },
]
// hemsoft: 布の帯を一枚の板に描いてから一度に重ねる。細切れのつなぎ目が半透明で重なると明るい縦の筋になり、毛羽に見えるため。
//   帯の先の薄れ (u) は絵の横の位置と同じなので、薄れを焼き込んだ絵を使い、濃さは帯ごとに一定で重ねる
//   (前を通る所 1・奥 0.75 は pass ごとに一定)。
const fadedCloth = new Map<ClothSrc, HTMLCanvasElement>()
function fadedClothOf(img: ClothSrc): HTMLCanvasElement {
  const had = fadedCloth.get(img)
  if (had) return had
  const [iw, ih] = clothSize(img)
  const c = document.createElement('canvas')
  c.width = iw; c.height = ih
  const g = c.getContext('2d')!
  g.drawImage(img, 0, 0)
  g.globalCompositeOperation = 'destination-in'
  const grad = g.createLinearGradient(0, 0, iw, 0)
  grad.addColorStop(0, 'rgba(0,0,0,1)'); grad.addColorStop(0.65, 'rgba(0,0,0,1)'); grad.addColorStop(1, 'rgba(0,0,0,0)')
  g.fillStyle = grad
  g.fillRect(0, 0, iw, ih)
  fadedCloth.set(img, c)
  return c
}
let stripCanvas: HTMLCanvasElement | null = null
function drawClothStrip(
  ctx: CanvasRenderingContext2D, img: ClothSrc, pts: Array<[number, number]>, th: (u: number) => number, alpha: number, keep: (i: number) => boolean,
): void {
  if (alpha <= 0.01) return
  const W = ctx.canvas.width, H = ctx.canvas.height
  stripCanvas ??= document.createElement('canvas')
  if (stripCanvas.width !== W || stripCanvas.height !== H) { stripCanvas.width = W; stripCanvas.height = H }
  const g = stripCanvas.getContext('2d')!
  g.setTransform(1, 0, 0, 1, 0, 0)
  g.clearRect(0, 0, W, H)
  g.setTransform(ctx.getTransform())
  drawClothAlong(g, fadedClothOf(img), pts, th, () => 1, keep, true)
  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.globalAlpha = Math.min(1, alpha)
  ctx.drawImage(stripCanvas, 0, 0)
  ctx.restore()
}
const HEM_SQUASH = 0.3
const HEM_SAMPLES = 28
const HEM_SLAM_S = 0.55   // だんで裾が開いて閉じるまで
const HEM_SLAM_LIFT = 0.55
function drawBrideHemDisc(
  ctx: CanvasRenderingContext2D, b: Boss, x: number, footY: number, h: number, pass: 'back' | 'front',
): void {
  const soft = windLook === 'hemsoft'
  const hemImgs = hemImgSets[hemSet()]
  const ghosts = soft ? HEM_GHOSTS_SOFT : HEM_GHOSTS
  const c = brideSpinClock(b)
  const slam = brideSlamAgeS(b)
  let theta: number, env: number, flare: number, pulse: number
  let lift = 1   // だんでは体がしゃがむので、胸元の帯も低くする (hem だけ)
  if (c && brideGhostOn()) {
    return   // 回る間は本人の残像だけ (drawBrideSpinGhosts)。布の尾 (drawBrideGhostTrails) は 11-02 作者 trail=none で呼ばない
  } else if (c) {
    env = brideSpinWindEnvelope(c)
    theta = brideSpinAngle(c)
    const frac = c.units - Math.floor(c.units)
    pulse = c.units < c.hits ? Math.exp(-frac / 0.16) : 0
    flare = 1 + 0.25 * pulse
  } else if (slam !== null && slam < HEM_SLAM_S) {
    theta = 0
    const k = slam / HEM_SLAM_S
    pulse = Math.exp(-slam / 0.12)
    flare = 1 + 0.55 * Math.exp(-slam / 0.2)
    env = Math.min(1, slam / 0.04) * (1 - k * k)
    if (windLook !== 'hemlow') lift = HEM_SLAM_LIFT
  } else {
    if (pass === 'front') drawBrideSlamScraps(ctx, b, x, footY, h, hemImgs)
    return
  }
  if (env <= 0.01) return
  const dir = b.facing === 'right' ? 1 : -1
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  ctx.translate(x, 0)
  ctx.scale(dir, 1)   // 絵の上の縁がいつも外を向くよう、点は右向きで作って全体を返す
  for (const L of windLook === 'hemlow' ? HEM_LAYERS_LOW : HEM_LAYERS) {
    for (let g = ghosts.length - 1; g >= 0; g--) {
      const G = ghosts[g]!
      if (L.img === 2 && g === 2) continue   // ヴェールの残像は1枚
      if (L.img === 2 && g > 0 && soft) continue   // hemsoft はヴェールの残像なし
      const ga = g === 0 ? 1 : G.a + (soft ? 0.15 : 0.3) * pulse
      const rx = h * L.r * flare * (1 + G.grow)
      const ry = rx * HEM_SQUASH
      const cy = footY - h * (L.cy * lift - 0.012 * g)
      for (let k = 0; k < L.n; k++) {
        const head = Math.PI / 2 - theta + (k * 2 * Math.PI) / L.n + 0.35 + G.lag
        const phis: number[] = [], pts: Array<[number, number]> = []
        for (let i = 0; i <= HEM_SAMPLES; i++) {
          const phi = head + L.len * (i / HEM_SAMPLES)
          phis.push(phi)
          pts.push([rx * Math.cos(phi), cy + ry * Math.sin(phi)])
        }
        const keep = (i: number) => (pass === 'front') === (Math.sin((phis[i]! + phis[i + 1]!) / 2) >= 0)
        const img = hemImgs[L.img === 0 ? (k + g) % 2 : 2]!
        if (soft) {
          drawClothStrip(ctx, img, pts, u => h * L.th * flare * (0.55 + 0.45 * Math.min(1, u / 0.1)) * (1 - 0.35 * u),
            env * ga * (pass === 'front' ? 1 : 0.75), keep)
          continue
        }
        drawClothAlong(ctx, img, pts,
          u => h * L.th * flare * (0.55 + 0.45 * Math.min(1, u / 0.1)) * (1 - 0.35 * u),
          u => env * ga * Math.min(1, (1 - u) / 0.35) * (Math.sin(phis[Math.round(u * HEM_SAMPLES)]!) >= 0 ? 1 : 0.75), keep)
      }
    }
  }
  ctx.restore()
  if (pass === 'front') drawBrideSlamScraps(ctx, b, x, footY, h, hemImgs)
}

// ---------------------------------------------------------------- ghost (11-02 作者の答え)
// 尾 (11-02 作者 trail=none で今は呼ばない。戻す時は drawBrideHemDisc の回る間に呼ぶ): 体の左右の縁 (腰・腿・肩の高さ) から外へ流れて消える布。付け根は体の縁から動かない (= 回らない)。
//   向いている側の尾は手前へ、反対側の尾は奥へ少し回り込む (回る向き = 手前が向いている側へ流れる)。
//   段の頭で外へ開いて濃くなり、回る速さに合わせて長さが少し揺れる。
// cy = 高さ (足元から・体の高さ比)・r0 = 付け根の半径・r1 = 先の半径・span = 回り込む角・th = 太さ・img = 絵 (0,1 裾・2 ヴェール)
const GHOST_TRAILS = [
  { cy: 0.22, r0: 0.2, r1: 0.62, span: 0.95, th: 0.26, img: 0 },
  { cy: 0.42, r0: 0.17, r1: 0.52, span: 0.8, th: 0.2, img: 1 },
  { cy: 0.7, r0: 0.12, r1: 0.4, span: 0.7, th: 0.13, img: 2 },
]
const GHOST_TRAIL_SAMPLES = 20
export function drawBrideGhostTrails(
  ctx: CanvasRenderingContext2D, b: Boss, c: BrideSpinClock, x: number, footY: number, h: number, pass: 'back' | 'front',
  imgs: (HTMLImageElement | null)[],
): void {
  const env = brideSpinWindEnvelope(c)
  if (env <= 0.01) return
  const theta = brideSpinAngle(c)
  const frac = c.units - Math.floor(c.units)
  const pulse = c.units < c.hits ? Math.exp(-frac / 0.16) : 0
  const dir = b.facing === 'right' ? 1 : -1
  ctx.save()
  ctx.translate(x, 0)
  ctx.scale(dir, 1)
  GHOST_TRAILS.forEach((T, ti) => {
    for (const side of [0, 1] as const) {
      // side 0 = 向いている側 (φ=0 から手前へ)・1 = 反対側 (φ=π から奥へ)。どちらも sin の符号で前後が決まる。
      if ((pass === 'front') !== (side === 0)) continue
      const sway = 1 + 0.12 * Math.sin(2 * theta + ti * 1.7 + side * 2.3)
      const len = T.span * sway * (1 + 0.25 * pulse)
      const r1 = T.r1 * (1 + 0.18 * pulse)
      const cy = footY - h * T.cy
      const pts: Array<[number, number]> = []
      for (let i = 0; i <= GHOST_TRAIL_SAMPLES; i++) {
        const u = i / GHOST_TRAIL_SAMPLES
        const phi = side * Math.PI + len * u
        const r = h * (T.r0 + (r1 - T.r0) * Math.pow(u, 0.7))
        pts.push([r * Math.cos(phi), cy + r * HEM_SQUASH * Math.sin(phi) - h * 0.05 * u * u])
      }
      const img = imgs[T.img]!
      drawClothStrip(ctx, img, pts, u => h * T.th * (1 + 0.15 * pulse) * (0.45 + 0.55 * Math.min(1, u / 0.25)) * (1 - 0.3 * u),
        env * (0.78 + 0.22 * pulse) * (side === 0 ? 1 : 0.8), () => true)
    }
  })
  ctx.restore()
}

// 残像: 少し前の向きの回る途中の絵を、白く薄く・少し横へ広げ・回る向きの後ろへずらして体の後ろに描く。正面の向きの残像は描かない
//   (正面の絵は描き方が別 = render.ts drawBossSprite)。lag = 何 ms 前の向きか。
const GHOST_BODIES = [
  { lag: 45, a: 0.4, grow: 0.04, shift: 0.025 },
  { lag: 90, a: 0.24, grow: 0.09, shift: 0.05 },
  { lag: 140, a: 0.12, grow: 0.15, shift: 0.08 },
]
// ghostdense = 残像を濃くした版 (同じ角度・ずれ・広がりで濃さだけ上げる)。0.82・0.60・0.38 も試したが、最後の段で残像が体の輪郭と溶けて白い塊に見えた。
const GHOST_BODIES_DENSE = [
  { lag: 45, a: 0.62, grow: 0.04, shift: 0.025 },
  { lag: 90, a: 0.42, grow: 0.09, shift: 0.05 },
  { lag: 140, a: 0.24, grow: 0.15, shift: 0.08 },
]
const ghostWhite = new Map<BrideTurnArt, HTMLCanvasElement>()
function whitenedTurnArt(art: BrideTurnArt, img: HTMLImageElement): HTMLCanvasElement {
  const had = ghostWhite.get(art)
  if (had) return had
  const cv = document.createElement('canvas')
  cv.width = img.naturalWidth; cv.height = img.naturalHeight
  const g = cv.getContext('2d')!
  g.drawImage(img, 0, 0)
  g.globalCompositeOperation = 'source-atop'
  g.fillStyle = 'rgba(232,230,242,0.62)'
  g.fillRect(0, 0, cv.width, cv.height)
  ghostWhite.set(art, cv)
  return cv
}
/** 本人の残像を描く (体より先)。frontRect = 同じ瞬間の正面の絵の不透明部分 (drawBrideTurnArt と同じ置き方)。 */
export function drawBrideSpinGhosts(
  ctx: CanvasRenderingContext2D, b: Boss, axisX: number, frontRect: { y: number; height: number },
): void {
  if (!brideGhostOn() || typeof document === 'undefined') return
  const bodies = windLook === 'ghostdense' ? GHOST_BODIES_DENSE : GHOST_BODIES
  const c = brideSpinClock(b)
  if (!c || c.ms <= 0) return
  const env = brideSpinWindEnvelope(c)
  if (env <= 0.01) return
  const unitsPerMs = c.units / c.ms
  const mulF = b.facing === 'right' ? -1 : 1
  const back = b.facing === 'right' ? -1 : 1   // 回る向きの後ろ (手前の面が向いている側へ流れる → 後ろは反対側)
  ctx.save()
  for (let g = bodies.length - 1; g >= 0; g--) {
    const G = bodies[g]!
    const units = c.units - G.lag * unitsPerMs
    if (units <= 0) continue
    const pick = brideTurnPick(brideSpinAngle({ ...c, units }))
    if (!pick) continue
    const img = brideTurnImage(pick.art)
    if (!img) continue
    const a = BRIDE_TURN_ART[pick.art]
    const s = frontRect.height / (a.hemY - a.headY)
    const mul = (pick.mirror ? -1 : 1) * mulF
    ctx.globalAlpha = G.a * env
    ctx.save()
    ctx.translate(axisX + back * frontRect.height * G.shift, frontRect.y + frontRect.height)
    ctx.scale(mul * s * (1 + G.grow), s)
    ctx.drawImage(whitenedTurnArt(pick.art, img), -a.axisX, -a.hemY)
    ctx.restore()
  }
  ctx.restore()
}

/** C の層 (白い風＋紫の帯) をまとめて描く。紫の帯は ?brideVeil=front の時は描かない。段ごとの三日月はどちらでも FX 側で出る。 */
export function drawBrideSpinLayers(
  ctx: CanvasRenderingContext2D, b: Boss, x: number, footY: number, h: number, pass: 'back' | 'front',
): void {
  if (hemOn()) {
    preloadBrideHem()
    if (hemReady()) { drawBrideHemDisc(ctx, b, x, footY, h, pass); return }
  }
  // 11-02 型の比較版: 体のまわりの風と紫の帯は出さない。ac は裾の輪、bc はだんの後の切れ端だけ。
  if (brideWindTypeOn() && !hemOn()) {
    preloadBrideWindCloth()
    if (clothReady()) {
      if (windLook === 'bc') { if (pass === 'front') drawBrideSlamScraps(ctx, b, x, footY, h); return }
      const c = brideSpinClock(b)
      const env = c ? brideSpinWindEnvelope(c) : 0
      if (c && env > 0.01) drawBrideHemRing(ctx, c, x, footY, h, b.facing === 'right' ? 1 : -1, env, pass)
      return
    }
  }
  // 11-01 比較版 (one / edge / lilac): 紫の一周の帯は描かない (one は2枚の布の帯だけ・edge と lilac は風だけ)。絵が読めるまでは今のまま。
  if (windLook === 'one' || windLook === 'edge' || windLook === 'lilac') {
    preloadBrideWindCloth()
    if (clothReady()) {
      if (windLook !== 'one') { drawBrideSpinWind(ctx, b, x, footY, h, pass); return }
      const c = brideSpinClock(b)
      const env = c ? brideSpinWindEnvelope(c) : 0
      if (c && env > 0.01) drawBrideOneVeil(ctx, c, x, footY, h, b.facing === 'right' ? 1 : -1, env, pass)
      return
    }
  }
  drawBrideSpinWind(ctx, b, x, footY, h, pass)
  if (veilStyle === 'orbit') drawBrideSpinOrbitVeil(ctx, b, x, footY, h, pass)
}

// URL の読み取りは最後 (上の let が初期化された後)。配布版はこの箱ごと消える = 常に 'ca'。
if (import.meta.env.DEV && typeof window !== 'undefined') {
  mode = parseBrideSpinMode(new URLSearchParams(window.location.search).get('brideSpin')) ?? 'ca'
  setBrideVeilStyle(new URLSearchParams(window.location.search).get('brideVeil'))
  setBrideWindLook(new URLSearchParams(window.location.search).get('brideWind'))
  ;(window as unknown as { __setBrideVeil?: (v?: unknown) => BrideVeilStyle }).__setBrideVeil = (v?: unknown) => setBrideVeilStyle(v)
  // 引数なしで今のモードを返す。知らない値は無視して今のモードを返す (検収パック kensyu-bride-spin.html が使う)。
  ;(window as unknown as { __setBrideSpin?: (v?: unknown) => BrideSpinMode }).__setBrideSpin = (v?: unknown) => setBrideSpinMode(v)
}
// 回る途中の絵は最初の連撃より前に読み込んでおく (読み込み前は正面の絵のまま回る)。
// 風の帯の絵も最初の連撃より前に読む (読み込み前は前の線の風で描く)。
if (typeof window !== 'undefined' && brideWindLook() === 'cloth') preloadBrideWindCloth()
if (typeof window !== 'undefined' && hemOn()) preloadBrideHem()
if (typeof window !== 'undefined' && mode === 'ca') for (const k of Object.keys(BRIDE_TURN_ART) as BrideTurnArt[]) brideTurnImage(k)
